<h1 mat-dialog-title>{{ injectData.dialogTitle }}</h1>
<form #form="ngForm" [formGroup]="addEditForm" (ngSubmit)="onFormDialogSubmit()">
  <div mat-dialog-content>
    <div class="dialog-grid">
      <div class="dialog-column">
        <span class="dialog-section mb-3"
          ><mat-icon>description</mat-icon> {{ 'system.dialogSection.generalInformation' | translate }}</span
        >
        <mat-dropdown
          [options]="store.workspaces()"
          [configurationInput]="workspaceDropdownSettings"
          [formControl]="addEditForm.controls.workspaceId"
          [isLoading]="(store.workspacesLoading$ | async) === store.ELoadStatus.loading"
          [isFormSubmitted]="form.submitted"
          (search)="getWorkspaces($event)"
        >
        </mat-dropdown>

        <mat-form-field appearance="outline">
          <mat-label>{{ 'field.templateName' | translate }}</mat-label>
          <input matInput [formControl]="addEditForm.controls.name" />
          <mat-error [control]="addEditForm.controls.name"></mat-error>
        </mat-form-field>

        <mat-dropdown
          [configurationInput]="paramLocationDropdownSettings"
          [options]="paramLocationDropdownOptions"
          [formControl]="addEditForm.controls.paramLocation"
          [isFormSubmitted]="form.submitted"
        >
        </mat-dropdown>

        <mat-form-field appearance="outline">
          <mat-label>{{ 'field.key' | translate }}</mat-label>
          <input matInput [formControl]="addEditForm.controls.key" />
          <mat-error [control]="addEditForm.controls.key"></mat-error>
        </mat-form-field>

        <mat-form-field appearance="outline" subscriptSizing="dynamic">
          <mat-label>{{ 'field.queryTemplate' | translate }}</mat-label>
          <textarea
            matInput
            rows="6"
            [formControl]="addEditForm.controls.queryTemplate"
            [placeholder]="'page.dataIntegrations.endpointParameters.queryTemplatePlaceholder' | translate"
          ></textarea>
          <mat-hint>{{ 'page.dataIntegrations.endpointParameters.queryTemplateHint' | translate }}</mat-hint>
          <mat-error [control]="addEditForm.controls.queryTemplate"></mat-error>
        </mat-form-field>

        @if (addEditForm.controls.queryTemplate.value) {
          <div class="query-preview">
            <span class="query-preview-label">{{
              'page.dataIntegrations.endpointParameters.livePreview' | translate
            }}</span>
            <div class="query-preview-content">
              @if (previewPrefix()) {
                <span class="query-preview-prefix">{{ previewPrefix() }}</span>
              }
              @for (segment of queryTemplateSegments(); track $index) {
                @if (segment.type === 'tag') {
                  <span class="query-preview-tag">{{ segment.value }}</span>
                } @else {
                  <span>{{ segment.value }}</span>
                }
              }
            </div>
          </div>
        }
      </div>

      <div class="dialog-column">
        <span class="dialog-section"
          ><mat-icon class="material-icons-outlined">link</mat-icon>
          {{ 'page.dataIntegrations.endpointParameters.parameterMappings' | translate }}
          @if (needsSync()) {
            <button mat-stroked-button type="button" class="sync-mappings-button mx-2" (click)="syncMappings()">
              <mat-icon class="material-icons-outlined">sync</mat-icon>
              {{ 'page.dataIntegrations.endpointParameters.syncMappings' | translate }}
            </button>
          }
        </span>
        <div class="dialog-section-header"></div>

        <div class="empty-tags-hint">
          {{ 'page.dataIntegrations.endpointParameters.noTagsHint' | translate }}
        </div>

        <div formArrayName="tags" class="tags-list">
          @for (tagGroup of addEditForm.controls.tags.controls; track tagGroup.controls.tag.value; let i = $index) {
            <fieldset [formGroupName]="i" class="tag-row">
              <legend class="tag-chip">{{ tagGroup.controls.tag.value }}</legend>

              <div class="tag-row-content">
                <div class="tag-field">
                  <mat-dropdown
                    [configurationInput]="fieldDropdownSettings"
                    [options]="fieldDropdownOptions()"
                    [formControl]="tagGroup.controls.field"
                    [isFormSubmitted]="form.submitted"
                  >
                  </mat-dropdown>
                </div>

                <div class="tag-operator">
                  <mat-dropdown
                    [configurationInput]="operatorDropdownSettings"
                    [options]="tagGroup.operatorOptions()"
                    [formControl]="tagGroup.controls.operator"
                    [isFormSubmitted]="form.submitted"
                  >
                  </mat-dropdown>
                </div>

                @if (tagGroup.isDateField()) {
                  <div class="tag-date-options">
                    @if (tagGroup.isShiftOutputVisible()) {
                      <div class="tag-date-option">
                        <mat-dropdown
                          [configurationInput]="shiftOutputDropdownSettings"
                          [options]="shiftOutputDropdownOptions"
                          [formControl]="tagGroup.controls.shiftOutput"
                          [isFormSubmitted]="form.submitted"
                        >
                        </mat-dropdown>
                      </div>
                    }

                    <div class="tag-date-option">
                      <mat-dropdown
                        [configurationInput]="dateFormatDropdownSettings"
                        [options]="tagGroup.availableDateFormats()"
                        [formControl]="tagGroup.controls.dateFormat"
                        [isFormSubmitted]="form.submitted"
                      >
                      </mat-dropdown>
                    </div>

                    @if (tagGroup.isDatePartVisible()) {
                      <div class="tag-date-option">
                        <mat-dropdown
                          [configurationInput]="datePartDropdownSettings"
                          [options]="datePartDropdownOptions"
                          [formControl]="tagGroup.controls.datePart"
                          [isFormSubmitted]="form.submitted"
                        >
                        </mat-dropdown>
                      </div>
                    }
                  </div>
                }

                @if (tagGroup.isSplitByVisible()) {
                  <mat-form-field appearance="outline" class="tag-split-by">
                    <mat-label>{{ 'field.splitBy' | translate }}</mat-label>
                    <input matInput [formControl]="tagGroup.controls.splitBy" />
                    <mat-error [control]="tagGroup.controls.splitBy"></mat-error>
                  </mat-form-field>
                }

                <div class="tag-value-wrappers">
                  <mat-form-field appearance="outline">
                    <mat-label>{{ 'field.valuePrefix' | translate }}</mat-label>
                    <input matInput [formControl]="tagGroup.controls.valuePrefix" />
                    <mat-error [control]="tagGroup.controls.valuePrefix"></mat-error>
                  </mat-form-field>

                  <mat-form-field appearance="outline">
                    <mat-label>{{ 'field.valueSuffix' | translate }}</mat-label>
                    <input matInput [formControl]="tagGroup.controls.valueSuffix" />
                    <mat-error [control]="tagGroup.controls.valueSuffix"></mat-error>
                  </mat-form-field>
                </div>

                <div class="tag-required">
                  <mat-dropdown
                    [configurationInput]="isRequiredDropdownSettings"
                    [options]="isRequiredDropdownOptions"
                    [formControl]="tagGroup.controls.isRequired"
                    [isFormSubmitted]="form.submitted"
                  >
                  </mat-dropdown>
                </div>
                <div class="tag-value-type">
                  <mat-dropdown
                    [configurationInput]="valueTypeDropdownSettings"
                    [options]="tagGroup.availableValueTypes()"
                    [formControl]="tagGroup.controls.valueType"
                    [isFormSubmitted]="form.submitted"
                  >
                  </mat-dropdown>
                </div>
              </div>
            </fieldset>
          }
        </div>
      </div>
    </div>
  </div>
  <mat-dialog-actions align="end">
    <button matButton="tonal" mat-dialog-close>{{ 'button.cancel' | translate }}</button>
    <button mat-flat-button type="submit" [disabled]="needsSync()">
      {{ injectData.submitButtonText }}
    </button>
  </mat-dialog-actions>
</form>
